import React, { useMemo } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import SortableStudent from './SortableStudent';
import type { Student, Vehicle, Schedule } from '../types';

interface DroppableTimeSlotProps {
  id: string;
  date: string;
  hour: number;
  type: 'pickup' | 'dropoff';
  students: Student[];
  vehicles: Vehicle[];
  schedules: Schedule[];
  onScheduleDelete?: (scheduleId: string) => void;
}

const DroppableTimeSlot: React.FC<DroppableTimeSlotProps> = ({
  id,
  date,
  hour,
  type,
  students,
  vehicles,
  schedules = [],
  onScheduleDelete
}) => {
  const { setNodeRef } = useDroppable({
    id: `${date}-${hour}-${type}`,
    data: { date, hour, type }
  });

  const sortableItems = useMemo(() => {
    if (!Array.isArray(schedules)) return [];
    return schedules
      .filter(schedule => 
        schedule && 
        schedule.date === date && 
        schedule.start_time === hour && 
        schedule.type === type
      )
      .map(schedule => schedule.student_id);
  }, [schedules, date, hour, type]);

  const studentsByVehicle = useMemo(() => {
    const groupedStudents = new Map<string, Array<{student: Student; schedule: Schedule}>>();
    
    if (!Array.isArray(schedules)) return groupedStudents;

    schedules.forEach(schedule => {
      if (!schedule || 
          schedule.date !== date || 
          schedule.start_time !== hour || 
          schedule.type !== type) return;
      
      const student = students.find(s => s.id === schedule.student_id);
      if (!student) return;
      
      const vehicleStudents = groupedStudents.get(schedule.vehicle_id) || [];
      vehicleStudents.push({ student, schedule });
      groupedStudents.set(schedule.vehicle_id, vehicleStudents);
    });
    
    return groupedStudents;
  }, [schedules, date, hour, type, students]);

  return (
    <div ref={setNodeRef} className="min-h-[100px] p-2 bg-gray-50 rounded-lg">
      <SortableContext 
        items={sortableItems}
        strategy={verticalListSortingStrategy}
      >
        <div className="flex flex-wrap gap-2">
          {Array.from(studentsByVehicle.entries()).map(([vehicleId, vehicleStudents]) => {
            const vehicle = vehicles.find(v => v.id === vehicleId);
            if (!vehicle) return null;

            return (
              <div 
                key={vehicleId}
                className="flex flex-col gap-1 p-2 rounded-lg"
                style={{ backgroundColor: vehicle.color + '20' }}
              >
                <div className="text-sm font-medium" style={{ color: vehicle.color }}>
                  {vehicle.name}
                </div>
                {vehicleStudents.map(({ student, schedule }) => (
                  <SortableStudent
                    key={student.id}
                    student={student}
                    vehicle={vehicle}
                    type={type}
                    onDelete={() => onScheduleDelete?.(schedule.id)}
                  />
                ))}
              </div>
            );
          })}
        </div>
      </SortableContext>
    </div>
  );
};

export default DroppableTimeSlot;